/**
 * shared/protocol.js — contrato de mensagens Backend ↔ Frontend (WAB Smart Town).
 *
 * Fonte única do PROTOCOLO v1 (ver docs/protocol.md). ESM puro, SEM dependências:
 * usado tanto pelo front (Vite) quanto pelo back (Node). Não importe three nem
 * nada de runtime aqui.
 *
 * Exporta:
 *   • constantes de tipos de mensagem (MSG) e enums (AVATAR_KIND, ACTION_KIND, …)
 *   • factories que montam o PAYLOAD de cada mensagem (sem envelope)
 *   • encode/decode do ENVELOPE (v/seq/ts) e validação leve de forma
 *
 * Navegação é client-authoritative: o backend manda intenções, o cliente resolve
 * o movimento. Por isso não há campos x/z aqui.
 */

export const PROTOCOL_VERSION = 1;

/* ----------------------------- tipos de mensagem ---------------------------- */
export const MSG = {
  // handshake / sessão
  HELLO: 'hello',       // C→S
  WELCOME: 'welcome',   // S→C
  SNAPSHOT: 'snapshot', // S→C
  PING: 'ping',         // S→C
  PONG: 'pong',         // C→S
  BATCH: 'batch',       // ambos
  // comandos S→C
  SPAWN: 'spawn',
  GOTO: 'goto',
  ACTION: 'action',
  EMOTE: 'emote',
  SAY: 'say',
  ANNOUNCE: 'announce', // fala do EDIFÍCIO (recepcionista): balão que SAI do prédio p/ todos
  LEAVE: 'leave',
  RESTYLE: 'restyle',   // troca o visual do avatar AO VIVO (reusa o efeito de transformação)
  SCORE: 'score',       // placar/missões (somatória do GRUPO de cidades) → HUD
  ACHIEVEMENT: 'achievement', // conquista de UM jogador (missão/frase) → banner no topo
  RELOAD: 'reload',     // admin manda os viewers recarregarem a página (telões)
  SIM: 'sim',           // parâmetros da SIMULAÇÃO no cliente (velocidade) — /vendor
  // eventos C→S
  ARRIVED: 'arrived',
  DONE: 'done',
  LEFT: 'left',
  WANDERING: 'wandering',
  NACK: 'nack'
};

export const KNOWN_TYPES = new Set(Object.values(MSG));

/** Sentido esperado de cada tipo (para roteamento/validação). `batch` em ambos. */
export const SERVER_TO_CLIENT = new Set([
  MSG.WELCOME, MSG.SNAPSHOT, MSG.PING,
  MSG.SPAWN, MSG.GOTO, MSG.ACTION, MSG.EMOTE, MSG.SAY, MSG.ANNOUNCE, MSG.LEAVE, MSG.RESTYLE, MSG.SCORE, MSG.ACHIEVEMENT, MSG.RELOAD, MSG.BATCH
]);
export const CLIENT_TO_SERVER = new Set([
  MSG.HELLO, MSG.PONG,
  MSG.ARRIVED, MSG.DONE, MSG.LEFT, MSG.WANDERING, MSG.NACK, MSG.BATCH
]);

/* --------------------------------- enums ----------------------------------- */
/** Avatar atual do jogador (entra de carro; vira pessoa ao estacionar). */
export const AVATAR_KIND = { CAR: 'car', PERSON: 'person' };
/** Modo de deslocamento no goto (auto = regra vizinho→a pé / longe→carro). */
export const GOTO_MODE = { AUTO: 'auto', WALK: 'walk', DRIVE: 'drive' };
/** Postura visual ao ficar em frente ao prédio (conjunto FECHADO). */
export const ACTION_KIND = {
  WAIT: 'wait', WORK: 'work', BROWSE: 'browse', TALK: 'talk',
  // posturas divertidas (animação do personagem parado em frente ao prédio)
  DANCE: 'dance', CELEBRATE: 'celebrate', EXCITED: 'excited', SPIN: 'spin', SIT: 'sit'
};
/** Resultado de uma action (1ª classe; pode derivar emoji/tom padrão). */
export const OUTCOME = { SUCCESS: 'success', FAIL: 'fail', PENDING: 'pending' };
/** Tom do balão de diálogo. */
export const SAY_TONE = { NEUTRAL: 'neutral', HAPPY: 'happy', SAD: 'sad', ALERT: 'alert' };
/** Códigos de recusa (nack). */
export const NACK_CODE = {
  UNKNOWN_BUILDING: 'unknown_building',
  UNKNOWN_PLAYER: 'unknown_player',
  BAD_MESSAGE: 'bad_message',
  UNSUPPORTED: 'unsupported'
};

/** Opções de aparência (fiéis a personModel.js / carModel.js) — o backend gera e
 *  persiste a aparência por usuário a partir destas listas. */
export const PERSON_APPEARANCE_OPTIONS = {
  hair: ['short', 'pompadour', 'spiky', 'long', 'curly', 'bald'],
  top: ['regata', 'tshirt', 'shirt', 'blazer', 'dress'],
  bottom: ['pants', 'shorts', 'skirt', 'none'], // 'none' quando top='dress'
  feet: ['shoes', 'flipflops', 'sandals']
};
export const CAR_APPEARANCE_OPTIONS = {
  model: ['defender', 'hilux', 'porsche', 'rav4', 'polestar', 'fusca'],
  wheels: ['alloy']
};

/* ------------------------------ factories ---------------------------------- */
/* Cada factory monta só o PAYLOAD ({ type, ... }). O envelope (v/seq/ts) é
   adicionado por encode(). Campos undefined são removidos (não serializam). */

/** Remove chaves com valor undefined (opcionais não enviados). */
function clean(obj) {
  for (const k of Object.keys(obj)) if (obj[k] === undefined) delete obj[k];
  return obj;
}

// --- comandos S→C ---
// `followKey` (opcional): chave OPACA de seguimento derivada do externalId
// (hash com salt secreto no servidor). Vai a TODOS os viewers, mas não revela o
// externalId (telefone) — permite à "visão do jogador" (?follow=<key>) travar a
// câmera no avatar dele sem expor PII. Ver server followKeyFor() e src/me.js.
export function spawn({ id, name, kind = AVATAR_KIND.CAR, color, appearance, entry = 'auto', followKey }) {
  return clean({ type: MSG.SPAWN, id, name, kind, color, appearance, entry, followKey });
}
export function goto({ id, building, mode = GOTO_MODE.AUTO }) {
  return clean({ type: MSG.GOTO, id, building, mode });
}
export function action({ id, kind = ACTION_KIND.WAIT, intent, outcome, durationMs, loop, params }) {
  return clean({ type: MSG.ACTION, id, kind, intent, outcome, durationMs, loop, params });
}
export function emote({ id, emoji, durationMs }) {
  return clean({ type: MSG.EMOTE, id, emoji, durationMs });
}
export function say({ id, text, durationMs, tone }) {
  return clean({ type: MSG.SAY, id, text, durationMs, tone });
}
/** Fala do EDIFÍCIO (não de um jogador): um balão de diálogo que "sai" do prédio
 *  `building` (id de grade A1..C5), como um recepcionista falando para todos os
 *  viewers da cidade. `title` = quem fala; OMITIDO, o balão sai sem cabeçalho. `color` =
 *  cor da bolha `#rrggbb` (padrão BRANCO; o texto se ajusta ao contraste). `tone`
 *  colore o NOME de quem fala. Não requer sessão/jogador — é global. */
export function announce({ building, text, title, tone, durationMs, color }) {
  return clean({ type: MSG.ANNOUNCE, building, text, title, tone, durationMs, color });
}
export function leave({ id, reason }) {
  return clean({ type: MSG.LEAVE, id, reason });
}
/** Troca o visual do avatar AO VIVO (jogador já em cena). O cliente reusa o efeito de
 *  transformação (carro↔pessoa), mesmo p/ o MESMO tipo (carro→carro / pessoa→pessoa). */
export function restyle({ id, appearance }) {
  return clean({ type: MSG.RESTYLE, id, appearance });
}
/** Placar/missões (somatórias acumuladas) → HUD de todos os viewers do GRUPO de
 *  cidades que divide o contador (Acquisition+Engagement+Conversion de um lado, a
 *  Mexican Town do outro — ver SCORE_GROUP em server/index.js).
 *  `missionsDone` = total de missões realizadas por TODOS os jogadores do grupo
 *  (somatória, SEM teto). */
export function score({ score, missionsDone }) {
  return clean({ type: MSG.SCORE, score, missionsDone });
}
/** Conquista de UM jogador (concluiu missão/frase) → banner de celebração no topo da
 *  tela dos viewers, com os avatares dele. `points` = quanto ele ganhou nesta conquista.
 *  `building` = o TIPO de edifício creditado, já RESOLVIDO pelo servidor (null quando não
 *  deu p/ resolver). Aditivo e retrocompatível — viewers antigos ignoram. */
/** `name`/`appearance`: quem celebrar, JUNTO no frame. O cliente resolvia isso pelo id no
 *  snapshot local de agentes — que no MESMO lote de um spawn ainda não existe, e aí o
 *  banner saía com "Jogador" e um avatar genérico exatamente na rajada do evento. */
export function achievement({ id, message, points, building, name, appearance }) {
  return clean({ type: MSG.ACHIEVEMENT, id, message, points, building, name, appearance });
}
/** Admin → viewers: recarregar a página (telões). `epoch` (ts) identifica ESTE
 *  reload: o cliente persiste antes do location.reload() e compara com o
 *  `reloadEpoch` do welcome ao reconectar — tela que perdeu o broadcast (aba
 *  congelada/conexão morta) se recarrega sozinha quando volta. */
export function reload({ epoch } = {}) {
  return clean({ type: MSG.RELOAD, epoch });
}

/**
 * Admin → viewers: parâmetros da SIMULAÇÃO que rodam no cliente.
 *
 * A navegação é client-authoritative, então velocidade é decisão do telão — mas quem
 * MANDA é o operador, e a escolha tem de valer para todas as telas da cidade ao mesmo
 * tempo. Daí ser broadcast (aplica na hora, sem recarregar) E ir no `welcome` (a tela que
 * abrir depois já nasce com o valor certo, sem um quadro na velocidade errada).
 *
 * `speed` é `{ car, person }` — MULTIPLICADORES, não valores absolutos. Multiplicador
 * porque a velocidade base é sorteada por avatar (carro 10–15, pessoa 2,8–3,8) e é essa
 * variação que faz o trânsito parecer trânsito; um valor absoluto achataria todo mundo no
 * mesmo passo. SEPARADO por tipo porque carro e pessoa vivem em escalas de tempo
 * diferentes, e acelerar o trânsito sem acelerar quem anda a pé (ou o contrário) é
 * justamente o ajuste que se quer no telão.
 */
export function sim({ speed }) {
  return clean({ type: MSG.SIM, speed });
}

// --- sessão ---
/** `reloadEpoch`: ts do último /reload da cidade (0 se nunca houve) — ver reload().
 *  `speed`: `{ car, person }` da simulação (ver sim()) — a tela já nasce na velocidade certa. */
export function welcome({ sessionId, city, serverTime = Date.now(), reloadEpoch, speed }) {
  return clean({ type: MSG.WELCOME, sessionId, city, serverTime, reloadEpoch, speed });
}
export function snapshot({ players }) {
  return { type: MSG.SNAPSHOT, players: players || [] };
}
export function ping(ts = Date.now()) {
  return { type: MSG.PING, ts };
}
export function hello({ city, clientType = 'viewer', resume = null, caps, token }) {
  return clean({ type: MSG.HELLO, clientType, city, resume, caps, token });
}
export function pong(ts = Date.now()) {
  return { type: MSG.PONG, ts };
}

// --- eventos C→S ---
export function arrived({ id, building, ts = Date.now() }) {
  return clean({ type: MSG.ARRIVED, id, building, ts });
}
export function done({ id, action: act, outcome, ts = Date.now() }) {
  return clean({ type: MSG.DONE, id, action: act, outcome, ts });
}
export function left({ id, ts = Date.now() }) {
  return { type: MSG.LEFT, id, ts };
}
export function wandering({ id }) {
  return { type: MSG.WANDERING, id };
}
export function nack({ refSeq, code, message }) {
  return clean({ type: MSG.NACK, refSeq, code, message });
}

// --- lote ---
export function batch(items) {
  return { type: MSG.BATCH, items: items || [] };
}

/* ------------------------------- envelope ---------------------------------- */

/**
 * Serializa um payload em um frame com envelope (v/seq/ts).
 * @param {object} msg   payload de uma factory
 * @param {{seq?:number, ts?:number}} env
 * @returns {string} JSON pronto para enviar
 */
export function encode(msg, { seq, ts = Date.now() } = {}) {
  return JSON.stringify(clean({ v: PROTOCOL_VERSION, seq, ts, ...msg }));
}

/**
 * BROADCAST: serializa o CORPO uma única vez e devolve uma função que só costura o
 * `seq` de cada conexão na frente. O `seq` é por conexão (o cliente usa para detectar
 * perda e para o `refSeq` do nack), então o caminho ingênuo era re-serializar a MESMA
 * mensagem para cada socket — com 200 telas numa cidade, cada comando do vendor custava
 * ~7,7 ms de event loop só de JSON.stringify repetido (medido: 38,6 µs por destinatário,
 * linear). Aqui o corpo é montado uma vez e a costura é uma concatenação de string.
 *
 * O frame resultante é byte-a-byte o mesmo que `encode` produziria.
 */
export function prepare(msg, { ts = Date.now() } = {}) {
  // O `ts` entra JUNTO com o corpo, na mesma ordem do encode (`{v, seq, ts, ...msg}`):
  // mensagens como o `ping` trazem o próprio `ts`, e a ordem de spread do JS faz o do
  // payload vencer sem mudar de posição. Costurá-lo por fora geraria a chave duas vezes.
  const corpo = JSON.stringify(clean({ ts, ...msg }));
  const prefixo = `{"v":${PROTOCOL_VERSION},"seq":`;
  return (seq) => prefixo + seq + ',' + corpo.slice(1);
}

/**
 * Faz parse de um frame recebido. Lança SyntaxError se o JSON for inválido.
 * @param {string|object} raw
 * @returns {object} a mensagem (envelope + payload)
 */
export function decode(raw) {
  return typeof raw === 'string' ? JSON.parse(raw) : raw;
}

/* ------------------------------ validação ---------------------------------- */

function req(msg, fields, errors) {
  for (const f of fields) {
    if (msg[f] === undefined || msg[f] === null || msg[f] === '') {
      errors.push(`campo obrigatório ausente: ${f}`);
    }
  }
}
function enumOk(value, enumObj, name, errors, optional = false) {
  if (value === undefined) { if (!optional) errors.push(`campo ausente: ${name}`); return; }
  const allowed = Object.values(enumObj);
  if (!allowed.includes(value)) errors.push(`${name} inválido: ${value} (esperado ${allowed.join('|')})`);
}

/**
 * Validação LEVE de forma (não valida regra de negócio). Devolve um array de
 * problemas — vazio = ok.
 * @param {object} msg
 * @returns {string[]}
 */
export function validate(msg) {
  if (!msg || typeof msg !== 'object') return ['mensagem não é um objeto'];
  const errors = [];
  const t = msg.type;
  if (!KNOWN_TYPES.has(t)) { errors.push(`tipo desconhecido: ${t}`); return errors; }

  switch (t) {
    case MSG.SPAWN:
      req(msg, ['id', 'name'], errors);
      enumOk(msg.kind, AVATAR_KIND, 'kind', errors, true);
      break;
    case MSG.GOTO:
      req(msg, ['id', 'building'], errors);
      enumOk(msg.mode, GOTO_MODE, 'mode', errors, true);
      break;
    case MSG.RESTYLE:
      req(msg, ['id', 'appearance'], errors);
      break;
    case MSG.ACTION:
      req(msg, ['id'], errors);
      enumOk(msg.kind, ACTION_KIND, 'kind', errors, true);
      enumOk(msg.outcome, OUTCOME, 'outcome', errors, true);
      break;
    case MSG.EMOTE:
      req(msg, ['id', 'emoji'], errors);
      break;
    case MSG.SAY:
      req(msg, ['id', 'text'], errors);
      enumOk(msg.tone, SAY_TONE, 'tone', errors, true);
      break;
    case MSG.ANNOUNCE: // fala do prédio: exige o prédio-alvo e o texto (sem `id` de jogador)
      req(msg, ['building', 'text'], errors);
      enumOk(msg.tone, SAY_TONE, 'tone', errors, true);
      break;
    case MSG.ACHIEVEMENT:
      req(msg, ['id', 'message'], errors);
      break;
    case MSG.LEAVE:
    case MSG.DONE:
    case MSG.LEFT:
    case MSG.WANDERING:
      req(msg, ['id'], errors);
      break;
    case MSG.ARRIVED:
      req(msg, ['id', 'building'], errors);
      break;
    case MSG.HELLO:
      req(msg, ['city'], errors);
      break;
    case MSG.WELCOME:
      req(msg, ['sessionId', 'city'], errors);
      break;
    case MSG.SNAPSHOT:
      if (!Array.isArray(msg.players)) errors.push('players deve ser um array');
      break;
    case MSG.NACK:
      req(msg, ['code'], errors);
      enumOk(msg.code, NACK_CODE, 'code', errors, true);
      break;
    case MSG.BATCH:
      if (!Array.isArray(msg.items)) { errors.push('items deve ser um array'); break; }
      msg.items.forEach((it, i) => validate(it).forEach((e) => errors.push(`items[${i}]: ${e}`)));
      break;
    case MSG.SCORE:   // todos os campos opcionais (números acumulados)
    case MSG.PING:
    case MSG.PONG:
      break;
  }
  return errors;
}

export function isValid(msg) {
  return validate(msg).length === 0;
}

/* ------------------------- agregado (import default) ------------------------ */
export default {
  PROTOCOL_VERSION, MSG, KNOWN_TYPES, SERVER_TO_CLIENT, CLIENT_TO_SERVER,
  AVATAR_KIND, GOTO_MODE, ACTION_KIND, OUTCOME, SAY_TONE, NACK_CODE,
  PERSON_APPEARANCE_OPTIONS, CAR_APPEARANCE_OPTIONS,
  spawn, goto, action, emote, say, announce, leave, restyle, score, achievement, reload,
  welcome, snapshot, ping, hello, pong, sim,
  arrived, done, left, wandering, nack, batch,
  encode, decode, validate, isValid
};
